'use client'

import { usePropertyRepresentativeCreate } from '@/hooks/data/property-representative'
import { useStripeSetupIntent } from '@/hooks/data/stripe'
import { PropertyRepresentationCreateFormField } from '@/types/property-representation'
import { TexasPropertySearchResult } from '@/types/texas-property'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Checkbox, Form, Input, notification, Select } from 'antd'
import { useEffect, useState } from 'react'
import Button from '../button'
import { SelectTexasProperty } from '../select-texas-property'
// import { useRouter } from 'next/navigation'

export default function PropertyRepresentationForm() {
  const [form] = Form.useForm<PropertyRepresentationCreateFormField>()
  const agreeToRepresentation = Form.useWatch('agree_to_representation', form)
  const [selectedProperties, setSelectedProperties] = useState<TexasPropertySearchResult[]>([])

  const [notificationApi, notificationContext] = notification.useNotification()

  const stripe = useStripe()
  const stripeElements = useElements()

  // const router = useRouter()

  const { onCreate } = usePropertyRepresentativeCreate()
  const { onCreate: stripeSetupIntent } = useStripeSetupIntent()

  const [isLoading, setIsLoading] = useState(false)
  const [isPaymentComplete, setIsPaymentComplete] = useState(false)

  useEffect(() => {
    if (!stripeElements) return
  }, [stripeElements])

  async function onSubmit() {
    try {
      setIsLoading(true)

      const data = await form.validateFields()
      if (!stripe || !stripeElements) {
        notificationApi.error({
          message: 'Error',
          description: 'Stripe has not loaded yet. Please try again in a moment.',
        })
        return
      }

      if (!isPaymentComplete) {
        notificationApi.error({
          message: 'Error',
          description: 'Please complete the payment form.',
        })
        return
      }

      // after create succeed
      const intent = await stripeSetupIntent({
        name: `${data.first_name} ${data.last_name}`,
        email: data.email,
      })

      await stripeElements.submit()

      await stripe.confirmSetup({
        elements: stripeElements,
        clientSecret: intent.data.secret,
        confirmParams: {
          return_url: `${window.location.origin}`,
        },
        redirect: 'if_required',
      })

      await onCreate({
        ...data,
        stripe_setup_intent_id: intent.data.id,
        properties: selectedProperties.map((property) => ({
          id: property.pid,
          appraisal_district_accountNumber: property.taxOfficeRef,
          city: property.city,
          address: property.streetPrimary,
          legal_description: property.legalDescription,
        })),
      })

      // using next js router causes dom child cleanup error
      // router.push('/thank-you')

      // so uses window.location.href for now
      window.location.href = '/thank-you'
    } catch (error) {
      if (error instanceof Error) {
        notificationApi.error({
          message: 'Error',
          description: error.message,
        })
      }

      notificationApi.error({
        message: 'Error',
        description: 'An unexpected error occurred. Please try again.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  function onSelectProperty(properties: TexasPropertySearchResult[]) {
    setSelectedProperties(properties)
  }

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit} scrollToFirstError className="w-full">
      {notificationContext}

      <div className="mb-4">
        <PaymentElement
          onChange={(e) => {
            setIsPaymentComplete(e.complete)
          }}
        />
      </div>

      <Form.Item
        label="First Name"
        name="first_name"
        rules={[{ required: true, message: 'Please enter your first name' }]}
      >
        <Input placeholder="First Name" />
      </Form.Item>

      <Form.Item
        label="Last Name"
        name="last_name"
        rules={[{ required: true, message: 'Please enter your last name' }]}
      >
        <Input placeholder="Last Name" />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: 'Please enter your email' },
          { type: 'email', message: 'Please enter a valid email' },
        ]}
      >
        <Input placeholder="Email" />
      </Form.Item>

      <Form.Item
        label="Phone Number"
        name="phone_number"
        rules={[{ required: true, message: 'Please enter your phone number' }]}
      >
        <Input placeholder="Phone Number" />
      </Form.Item>

      <Form.Item label="County" name="county" rules={[{ required: true, message: 'Please select the county' }]}>
        <Select placeholder="Select County" getPopupContainer={(trigger) => trigger.parentElement!}>
          <Select.Option value="Travis Central Appraisal District">Travis Central Appraisal District</Select.Option>
          <Select.Option value="Bexar Central Appraisal District">Bexar Central Appraisal District</Select.Option>
        </Select>
      </Form.Item>

      <SelectTexasProperty formName="property_id" formLabel="Select Property" onSelect={onSelectProperty} />

      <Form.Item
        label="Referred By"
        name="referred_by"
        rules={[
          {
            required: false,
            message: 'Please enter the name of the person who referred you',
            min: 1,
          },
        ]}
      >
        <Input placeholder="Referred By" />
      </Form.Item>

      <Form.Item
        name="agree_to_representation"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) => (value ? Promise.resolve() : Promise.reject('You must agree to representation')),
          },
        ]}
      >
        <Checkbox>I agree to representation</Checkbox>
      </Form.Item>

      <Button
        text="Submit"
        type="submit"
        disabled={!agreeToRepresentation || !isPaymentComplete}
        className="rounded md:px-5! md:py-2! mt-2 mb-6"
        loading={isLoading}
      />

      <p className="text-sm text-gray-600">
        By submitting this form, I agree to be contacted by Pinnacle Property Tax, LLC via call, email, and text
        (including automated messages) regarding property tax services. Consent is not a condition of purchase. Message
        and data rates may apply. I understand I can opt out at any time. I also agree to the Terms of Service and
        Privacy Policy.
      </p>
    </Form>
  )
}
